import { Module, ActionTree, MutationTree, GetterTree } from "vuex"
import { RootState } from "@/store/RootStore"
import { make } from "vuex-pathify"
import router from "@/router/Router"
import { User, UserCreate, Utils } from "rp-components"
import { StoreUtils } from "@/utils/StoreUtils"
import Services from "@/services/Services"
import { Route } from "vue-router"
import { Credential } from "rp-components"
import { ApiError, LoginState as LoginStateEnum } from "rp-components"
import { AxiosError } from "axios"
import { clearPendingPassId } from "@/utils/WalletPendingPassStorage"

const namespaced: boolean = true

//===================================
// State
//===================================
export interface LoginState {
    user?: User
    refererRoute?: Route
    state: LoginStateEnum
    tenantSelections: Map<string, string>[]
}

const defaultState = () => {
    return <LoginState>{
        user: undefined,
        refererRoute: undefined,
        state: LoginStateEnum.LOGIN,
        tenantSelections: []
    }
}

export const state: LoginState = defaultState()

//===================================
// Getters
//===================================
const getters: GetterTree<LoginState, RootState> = {
    ...make.getters(state)
}

//===================================
// Actions
//===================================
export const actions: ActionTree<LoginState, RootState> = {
    ...make.actions(state),

    resetState({ commit }) {
        commit("resetState")
    },

    async login({ dispatch }, credential: Credential) {
        await StoreUtils.withLoading(
            dispatch,
            async () => {
                await Services.auth.login(credential)
                const user = await Services.auth.getUser()
                dispatch("setUser", user)
                const path = state.refererRoute ? state.refererRoute.fullPath : "/public/properties"
                router.replace(path)
            },
            async (err: any) => {
                const axiosErr = err as AxiosError | undefined
                const apiError = axiosErr?.response?.data as ApiError | undefined
                if (apiError && apiError.errorType == "NEW_PASSWORD_REQUIRED") {
                    dispatch("setState", LoginStateEnum.FORCE_CHANGE_PASSWORD)
                    return true
                }
                return false
            }
        )
    },

    async logout({ commit }) {
        await Services.auth.logout()
        clearPendingPassId()
        commit("wallet/resetState", null, { root: true })
        router.push("/public/login")
    },

    async loadUser({ commit }) {
        const user = await Services.auth.getUser()
        commit("SET_USER", user)
    },

    async resetPassword({ dispatch }, payload: { credential: Credential }) {
        await StoreUtils.withLoading(dispatch, async () => {
            await Services.auth.resetPassword(payload.credential)
            dispatch("setState", LoginStateEnum.PASSWORD_RESET_SUCCESS)
        })
    },

    async setPassword({ dispatch }, payload: { credential: Credential }) {
        await StoreUtils.withLoading(dispatch, async () => {
            await Services.auth.setPassword(payload.credential)
            dispatch("setState", LoginStateEnum.LOGIN)
        })
    },

    async registerAccount({ dispatch }, payload: { user: UserCreate }) {
        const registerAccountAction = async () => {
            await Services.auth.registerAccount(payload.user)
            dispatch("setState", LoginStateEnum.REGISTER_ACCOUNT_SUCCESS)
        }

        const errorHandler = async (err: any) => {
            const axiosError = err as AxiosError | undefined
            const apiError = axiosError?.response?.data as ApiError | undefined
            if (apiError?.errorType != "TENANT_SELECTION_REQUIRED") {
                return false
            }
            const tenantSelections = await Services.auth.getTenants(payload.user.email)
            const tenantSelectionsAsJson = tenantSelections.map((selection) => Utils.stringifyJson(selection))
            const deduplicatedSelections = [...new Set(tenantSelectionsAsJson)].map((selection) => Utils.parseJson(selection))
            await dispatch("setTenantSelections", deduplicatedSelections)
            await dispatch("setState", LoginStateEnum.REGISTER_ACCOUNT_TENANT_SELECTION)
            return true
        }

        await StoreUtils.withLoading(
            dispatch,
            async () => {
                await registerAccountAction()
            },
            errorHandler
        )
    }
}

//===================================
// Mutators
//===================================
const mutations: MutationTree<LoginState> = {
    ...make.mutations(state),

    resetState(state) {
        const reset = defaultState()
        state.user = reset.user
    }
}

//===================================
// Module
//===================================
export const login: Module<LoginState, RootState> = {
    namespaced,
    state: state,
    getters: getters,
    actions: actions,
    mutations: mutations
}
